/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { EnvironmentProviders, ErrorHandler, inject, Provider, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import * as Sentry from '@sentry/angular';

export function isSentryEnabled(): boolean {
  return !!environment.sentryDsn;
}

export function initSentry(): void {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.sentryEnvironment,
    release: environment.releaseHash,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

export function setSentryUser(user: {
  '@id'?: string;
  email?: string;
  shortHand?: string;
} | null | undefined): void {
  if (!isSentryEnabled() || !user) {
    return;
  }

  Sentry.setUser({
    id: user['@id'],
    email: user.email,
    username: user.shortHand,
  });
}

export function clearSentryUser(): void {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.setUser(null);
}

export function getSentryProviders(): (Provider | EnvironmentProviders)[] {
  if (!isSentryEnabled()) {
    return [];
  }

  return [
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ];
}
